import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ToastContainer } from "react-toastify";
import Loader from "../Constant/Loader";
import AdminAppBar from "../adminComponents/AdminAppBar";
import BackArrows from "../Form/BackArrow/BackArrows";
import { MdOutlineDateRange } from "react-icons/md";
import { getIcons } from "../Constant/Constant";
import LeadDbService from "../Services/LeadDbService";
import teamService from "../Services/TeamService";
import { showErrorToast } from "../Constant/Notification";
import AllLeadFilter from "./AllLeadFilter";
import { teamIds, teamList, userRole } from "../../utils/storage";

const AssignSummary = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedUser, setSelectedUser] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [searchMember, setSearchMember] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [teamDropdownVisible, setTeamDropdownVisible] = useState(false);

  const [leads, setLeads] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [prevPageToken, setPrevPageToken] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [totalLeads, setTotalLeads] = useState(0);
  const [hasMoreLeads, setHasMoreLeads] = useState(false);
  const [cachedPages, setCachedPages] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const Icons = getIcons();
  const dropdownRef = useRef(null);
  const userDropdownRef = useRef(null);

  
  useEffect(() => {
    const getTeamList = async () => {
      try {
        setLoading(true);
        const response = await teamService.fetchTeams();
        const fetchedTeams = response?.data || [];
        setTeams(fetchedTeams);
        setFilteredTeams(fetchedTeams);

        
        if (userRole === "BOD" && teamIds?.length > 0) {
          const defaultTeam = fetchedTeams.find((team) =>
            teamIds.includes(team.teamId) && team.BOD?.length > 0
          ) || fetchedTeams[0] || null;
          setSelectedTeam(defaultTeam);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching teams:", error);
        showErrorToast("Failed to fetch teams. Please try again.");
        setLoading(false);
      }
    };
    getTeamList();
  }, []);


  useEffect(() => {
    const filtered = teams.filter((team) =>
      team.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredTeams(filtered);
  }, [searchQuery, teams]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setTeamDropdownVisible(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  const getTeamMembers = () => {
    if (!selectedTeam) return [];

    return [
      ...(selectedTeam.BOD || []),
      ...(selectedTeam.MD?.id && selectedTeam.MD?.name ? [selectedTeam.MD] : []),
      ...(selectedTeam.members || []),
    ].filter((member) =>
      member.name?.toLowerCase().includes(searchMember.toLowerCase())
    );
  };

  const filteredMembers = getTeamMembers();

  const handleSelectTeam = (team) => {
    setSelectedTeam(team);
    setSelectedUser(""); 
    setShowUserDropdown(false);
    setTeamDropdownVisible(false);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setShowUserDropdown(false);
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
    setNextPageToken(null);
    setPrevPageToken(null);
    setCachedPages({}); // Clear cache when status changes
    if (selectedTeam && selectedUser) {
      handleLoadData(null, null);
    }
  };

  // Reset cache when date, team, or user changes
  useEffect(() => {
    setCachedPages({});
  }, [startDate, endDate, selectedTeam, selectedUser]);

  const formatDate = (date) => {
    return date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
      : "";
  };

  const generateCacheKey = (pageNumber, status) => {
    return `${pageNumber}-${selectedTeam?.teamId || ""}-${selectedUser?.id || ""}-${formatDate(startDate)}-${formatDate(endDate)}-${status}`;
  };

  const handleLoadData = async (token = null, direction = null) => {
    if (!selectedTeam || !selectedUser) {
      showErrorToast("Please select team and user");
      return;
    }

    setLoading(true);
    try {
      const formattedStartDate = formatDate(startDate);
      const formattedEndDate = formatDate(endDate);
      const pageNumber =
        direction === "next" ? currentPage + 1 : direction === "previous" ? currentPage - 1 : 1;
      const cacheKey = generateCacheKey(pageNumber, selectedStatus);

      if (cachedPages[cacheKey]) {
        setLeads(cachedPages[cacheKey].data);
        setNextPageToken(cachedPages[cacheKey].next || null);
        setPrevPageToken(cachedPages[cacheKey].previous || null);
        setCurrentPage(pageNumber);
        setHasMoreLeads(cachedPages[cacheKey].hasMore);
        setTotalLeads(cachedPages[cacheKey].totalCount || 0);
        setLoading(false);
        return;
      }

      const params = {
        teamId: selectedTeam.teamId,
        uid: selectedUser.id,
        stDate: formattedStartDate,
        edDate: formattedEndDate,
        status: selectedStatus !== "All" ? selectedStatus : undefined,
        next: direction === "next" ? token : null,
        previous: direction === "previous" ? token : null,
        direction: direction,
      };

      const response = await LeadDbService.getAssignSummaryList(params);
      const data = Array.isArray(response.data?.leadsArray) ? response.data.leadsArray : [];

      setLeads(data);
      setTotalLeads(response.data?.maxCount || 0);
      setNextPageToken(response.data?.next || null);
      setPrevPageToken(response.data?.previous || null);
      setCurrentPage(pageNumber);

      const hasMore = data.length === 10 && pageNumber * 10 < response.data?.maxCount;
      setHasMoreLeads(hasMore);
      setCachedPages((prev) => ({
        ...prev,
        [cacheKey]: {
          data,
          next: response.data?.next || null,
          previous: response.data?.previous || null,
          hasMore,
          totalCount: response.data?.maxCount || 0,
        },
      }));
    } catch (error) {
      console.error("Error loading summary data:", error);
      showErrorToast("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (token, direction) => {
    handleLoadData(token, direction);
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      {loading && <Loader />}
      <AdminAppBar />
      <ToastContainer />

      <div className="h-full py-20">
        <div className="px-8 flex pt-9 items-center">
          <BackArrows onClick={() => navigate(-1)} />
          <div className="font-semibold md:mx-0 cursor-default text-3xl lg:mx-6 mx-4">
            Assign Summary
          </div>
        </div>

        <div className="mx-8 mt-9 bg-white rounded-xl shadow-sm px-16 py-20">
          <div className="flex justify-between items-start mb-8">
            <div className="space-y-4 w-1/2">
              <div className="flex justify-between gap-6 max-w-[488px] w-full">
                <div className="relative w-[230px]">
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg bg-white text-gray-700 cursor-pointer"
                    placeholderText="Choose Start Date"
                    maxDate={new Date()}
                    selectsStart
                    dateFormat="dd/MM/yyyy"
                    startDate={startDate}
                    endDate={endDate}
                  />
                  <MdOutlineDateRange className="absolute right-6 top-1/2 transform -translate-y-1/2 text-gray-500" />
                </div>

                <div className="relative w-[230px]">
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg bg-white text-gray-700 cursor-pointer"
                    placeholderText="Choose End Date"
                    maxDate={new Date()}
                    selectsEnd
                    dateFormat="dd/MM/yyyy"
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                  />
                  <MdOutlineDateRange className="absolute right-6 top-1/2 transform -translate-y-1/2 text-gray-500" />
                </div>
              </div>

              <div className="flex flex-col relative">
                <button
                disabled={userRole === "BOD"}
                  className="w-[488px] flex justify-between items-center px-4 py-2 border-2 border-gray-300 rounded-lg bg-white text-left"
                  onClick={() => setTeamDropdownVisible(!teamDropdownVisible)}
                >
                  <span className="text-gray-700">
                    {selectedTeam ? selectedTeam.name : "Select Team"}
                  </span>
                  <svg
                    className="h-5 w-5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {teamDropdownVisible && (
                  <div
                    ref={dropdownRef}
                    className="absolute z-10 mt-14 w-[488px] bg-white border-2 divide-y divide-gray-200 rounded-lg shadow max-h-48 overflow-y-auto"
                  >
                    <div className="w-full px-3 py-3 top-0 bg-white">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search Team"
                        className="w-full px-4 py-2 rounded-md text-sm border border-gray-300"
                      />
                    </div>

                    <ul className="py-2 text-sm text-gray-700">
                      {filteredTeams.length > 0 ? (
                        filteredTeams.map((team) => (
                          <li key={team.teamId}>
                            <div
                              onClick={() => handleSelectTeam(team)}
                              className="block font-primaryFont cursor-pointer px-4 py-2 hover:bg-gray-100"
                            >
                              {team.name}
                            </div>
                          </li>
                        ))
                      ) : (
                        <li className="px-4 py-2 text-gray-500">
                          No teams found
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex flex-col relative">
                <button
                  className={`w-[488px] flex justify-between items-center px-4 py-2 border-2 ${
                    selectedTeam ? "border-gray-300" : "border-gray-200 bg-gray-50"
                  } rounded-lg bg-white text-left`}
                  onClick={() => {
                    if (selectedTeam) setShowUserDropdown(!showUserDropdown);
                  }}
                  disabled={!selectedTeam}
                >
                  <span className={`${selectedTeam ? "text-gray-700" : "text-gray-400"}`}>
                    {selectedUser ? selectedUser.name : "Select User"}
                  </span>
                  <svg
                    className={`h-5 w-5 ${selectedTeam ? "text-gray-400" : "text-gray-300"}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {showUserDropdown && selectedTeam && (
                  <div
                    ref={userDropdownRef}
                    className="absolute z-10 mt-14 w-[488px] bg-white border-2 border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                  >
                    <div className="w-full px-3 py-3 top-0 bg-white">
                      <input
                        type="text"
                        value={searchMember}
                        onChange={(e) => setSearchMember(e.target.value)}
                        placeholder="Search Member"
                        className="w-full px-4 py-2 rounded-md text-sm border border-gray-300"
                      />
                    </div>

                    <ul className="py-2 text-sm text-gray-700">
                      {filteredMembers.length > 0 ? (
                        filteredMembers.map((member) => (
                          <li key={member.id}>
                            <div
                              onClick={() => handleSelectUser(member)}
                              className="block font-primaryFont cursor-pointer px-4 py-2 hover:bg-gray-100"
                            >
                              {member.name} {member.id === selectedTeam?.MD?.id ? "(MD)" : member.BOD?.some(b => b.id === member.id) ? "(BOD)" : ""}
                            </div>
                          </li>
                        ))
                      ) : (
                        <li className="px-4 py-2 text-gray-500">
                          No members found
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex justify-end w-[488px] mt-4 pt-4">
                <button
                  className={`w-[217.7px] ${
                    selectedTeam && selectedUser && endDate && startDate
                      ? "bg-customGreen hover:bg-green-600"
                      : "bg-gray-300 cursor-not-allowed"
                  } text-white font-medium py-2 px-4 rounded-md transition duration-200`}
                  onClick={() => handleLoadData()}
                  disabled={!selectedTeam || !selectedUser || !endDate || !startDate}
                >
                  Load
                </button>
              </div>

              <img src={Icons?.assignImage} alt="Assign Summary" />
            </div>
            <div className="w-1/2 shadow-md p-2 rounded-md py-3">
              <AllLeadFilter
                selectedTeam={leads}
                totalLeadCount={totalLeads}
                nextPageToken={nextPageToken}
                prevPageToken={prevPageToken}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onStatusChange={handleStatusChange}
                selectedStatus={selectedStatus}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignSummary;